import { Component } from '@theme/component';

/**
 * Suggested reading-add power by age band (not mandatory — the patient can
 * still type any value in the Add fields). Ranges per the optical guidance
 * this store follows.
 */
const ADD_BY_AGE = [
  { min: 39, max: 40, add: 1.0 },
  { min: 41, max: 42, add: 1.25 },
  { min: 43, max: 45, add: 1.5 },
  { min: 46, max: 48, add: 1.75 },
  { min: 49, max: 50, add: 2.0 },
  { min: 51, max: 52, add: 2.25 },
  { min: 53, max: 55, add: 2.5 },
  { min: 56, max: 58, add: 2.75 },
  { min: 59, max: Infinity, add: 3.0 },
];

/**
 * @typedef {object} PrescriptionBuilderRefs
 * @property {HTMLInputElement} rxYesInput
 * @property {HTMLInputElement} rxNoInput
 * @property {HTMLElement} rxFields
 * @property {HTMLInputElement} screensInput
 * @property {HTMLInputElement} sunInput
 * @property {HTMLInputElement} drivingInput
 * @property {HTMLElement} recommendationBanner
 * @property {HTMLElement} recommendationText
 * @property {HTMLInputElement} recommendationField
 * @property {HTMLElement} upsellCard
 * @property {HTMLInputElement} ageInput
 * @property {HTMLElement} ageSuggestion
 * @property {HTMLInputElement} addSuggestionField
 * @property {HTMLInputElement} cylOdInput
 * @property {HTMLInputElement} axisOdInput
 * @property {HTMLElement} axisOdHint
 * @property {HTMLInputElement} cylOiInput
 * @property {HTMLInputElement} axisOiInput
 * @property {HTMLElement} axisOiHint
 * @property {HTMLInputElement} confirmRxInput
 */

/**
 * Drives the eyewear prescription/lifestyle questionnaire on the product page:
 * toggles the Rx fields, computes soft lens-type recommendations from the
 * lifestyle answers, and suggests a reading-add power from age. Everything
 * computed here is written into hidden `properties[...]` inputs so it rides
 * along into the cart line item without touching the base add-to-cart form.
 * @extends Component<PrescriptionBuilderRefs>
 */
class PrescriptionBuilder extends Component {
  handleRxToggle() {
    const needsRx = this.refs.rxYesInput?.checked ?? true;
    if (this.refs.rxFields) this.refs.rxFields.hidden = !needsRx;
    if (this.refs.confirmRxInput) this.refs.confirmRxInput.required = needsRx;
  }

  /**
   * Cylinder (CYL) and Axis are optically inseparable: a cylinder value without
   * an axis is meaningless. Whenever either eye's CYL field has a value, that
   * eye's Axis field becomes required — enforced via native form validation
   * since these inputs ride along via the `form` attribute.
   * @param {Event} event
   */
  handleCylInput(event) {
    const cylInput = /** @type {HTMLInputElement} */ (event.target);
    const isRightEye = cylInput === this.refs.cylOdInput;
    const axisInput = isRightEye ? this.refs.axisOdInput : this.refs.axisOiInput;
    const axisHint = isRightEye ? this.refs.axisOdHint : this.refs.axisOiHint;
    if (!axisInput) return;

    const cylHasValue = cylInput.value.trim() !== '';
    axisInput.required = cylHasValue;
    if (axisHint) axisHint.hidden = !cylHasValue;
  }

  handleAnswerChange() {
    this.#updateRecommendation();
  }

  handleAgeInput() {
    const age = Number(this.refs.ageInput?.value);
    const { ageSuggestion, addSuggestionField } = this.refs;
    if (!ageSuggestion || !addSuggestionField) return;

    const band = Number.isFinite(age) ? ADD_BY_AGE.find((b) => age >= b.min && age <= b.max) : undefined;

    if (band) {
      const addValue = `+${band.add.toFixed(2)}`;
      const template = ageSuggestion.dataset.template || '{add}';
      ageSuggestion.textContent = template.replace('{add}', addValue);
      ageSuggestion.hidden = false;
      addSuggestionField.value = addValue;
    } else {
      ageSuggestion.hidden = true;
      ageSuggestion.textContent = '';
      addSuggestionField.value = '';
    }
  }

  connectedCallback() {
    super.connectedCallback();
    this.handleRxToggle();
    this.#syncAxisRequired(this.refs.cylOdInput, this.refs.axisOdInput, this.refs.axisOdHint);
    this.#syncAxisRequired(this.refs.cylOiInput, this.refs.axisOiInput, this.refs.axisOiHint);
  }

  /**
   * @param {HTMLInputElement} [cylInput]
   * @param {HTMLInputElement} [axisInput]
   * @param {HTMLElement} [axisHint]
   */
  #syncAxisRequired(cylInput, axisInput, axisHint) {
    if (!cylInput || !axisInput) return;
    const cylHasValue = cylInput.value.trim() !== '';
    axisInput.required = cylHasValue;
    if (axisHint) axisHint.hidden = !cylHasValue;
  }

  #updateRecommendation() {
    const { screensInput, sunInput, drivingInput, recommendationBanner, recommendationText, recommendationField, upsellCard } =
      this.refs;

    const screens = screensInput?.checked ?? false;
    const sun = sunInput?.checked ?? false;
    const driving = drivingInput?.checked ?? false;

    /** @type {string[]} */
    const messages = [];
    /** @type {string[]} */
    const tags = [];

    if (driving) {
      messages.push(this.dataset.msgDriving || '');
      tags.push('Transitions XTRActive');
    } else if (sun) {
      messages.push(this.dataset.msgSun || '');
      tags.push('Transitions');
    }

    if (screens) {
      messages.push(this.dataset.msgScreens || '');
      tags.push('Blue Block');
    }

    const hasRecommendation = messages.length > 0;

    if (recommendationBanner) recommendationBanner.hidden = !hasRecommendation;
    if (recommendationText) recommendationText.textContent = messages.filter(Boolean).join(' ');
    if (recommendationField) recommendationField.value = tags.join(', ');

    if (upsellCard) {
      const hasNightProduct = upsellCard.dataset.hasNightProduct === 'true';
      upsellCard.hidden = !(screens && hasNightProduct);
    }
  }
}

if (!customElements.get('prescription-builder-component')) {
  customElements.define('prescription-builder-component', PrescriptionBuilder);
}
